import { useMemo, useState } from 'react';
import type { EvmSummary } from '../../services/analytics';
import {
  ChartEmpty,
  formatCompact,
  formatCurrency,
  niceTicks,
  useMeasure,
} from './chartPrimitives';

interface EvmCurveProps {
  evm: EvmSummary;
  dataDate: Date | null;
}

const SERIES = [
  {
    key: 'planned' as const,
    label: 'Planned (BCWS)',
    colour: 'var(--text-muted)',
    dash: '4 3',
  },
  { key: 'earned' as const, label: 'Earned (BCWP)', colour: 'var(--mr-vivid-blue)', dash: undefined },
  { key: 'actual' as const, label: 'Actual (ACWP)', colour: 'var(--status-warn)', dash: undefined },
];

/**
 * The three-curve earned value S-curve.
 *
 * Earned and actual stop at the data date rather than running flat to the end
 * of the plan — a flat line to the horizon reads as "no progress" instead of
 * "no data", which is the opposite of the truth.
 */
export function EvmCurve({ evm, dataDate }: EvmCurveProps) {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const { curve, hasCost } = evm;
  const format = hasCost ? formatCurrency : (v: number) => `${formatCompact(v)} act`;

  const maxValue = useMemo(
    () =>
      curve.reduce(
        (max, point) =>
          Math.max(max, point.planned, point.earned ?? 0, point.actual ?? 0),
        0
      ),
    [curve]
  );

  if (curve.length < 2) {
    return <ChartEmpty message="Not enough dated activity to build an S-curve." />;
  }

  const height = 300;
  const margin = { top: 16, right: 18, bottom: 40, left: 58 };
  const plotWidth = Math.max(0, width - margin.left - margin.right);
  const plotHeight = height - margin.top - margin.bottom;

  const ticks = niceTicks(maxValue, 5);
  const yMax = ticks[ticks.length - 1] || 1;

  const xFor = (index: number) => (index / (curve.length - 1)) * plotWidth;
  const yFor = (value: number) => plotHeight - (value / yMax) * plotHeight;

  const buildPath = (key: 'planned' | 'earned' | 'actual') => {
    let path = '';
    let started = false;
    curve.forEach((point, index) => {
      const value = point[key];
      if (value === null || value === undefined) return;
      path += `${started ? 'L' : 'M'}${xFor(index).toFixed(1)},${yFor(value).toFixed(1)}`;
      started = true;
    });
    return path;
  };

  const dataDateIndex = dataDate
    ? curve.findIndex(
        (point) =>
          point.date.getFullYear() === dataDate.getFullYear() &&
          point.date.getMonth() === dataDate.getMonth()
      )
    : -1;

  // Label every nth month so the axis never collides with itself.
  const labelStride = Math.max(1, Math.ceil(curve.length / Math.max(4, plotWidth / 62)));
  const hovered = hoverIndex !== null ? curve[hoverIndex] : null;

  return (
    <div ref={ref} className="w-full">
      {width > 0 && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1.5">
            {SERIES.map((series) => (
              <span key={series.key} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                <svg width="18" height="8" aria-hidden="true">
                  <line
                    x1="0"
                    y1="4"
                    x2="18"
                    y2="4"
                    stroke={series.colour}
                    strokeWidth="2"
                    strokeDasharray={series.dash}
                  />
                </svg>
                {series.label}
              </span>
            ))}
          </div>

          <svg
            width={width}
            height={height}
            role="img"
            aria-label="Earned value S-curve"
            onMouseLeave={() => setHoverIndex(null)}
          >
            <g transform={`translate(${margin.left},${margin.top})`}>
              {ticks.map((tick) => {
                const y = yFor(tick);
                return (
                  <g key={tick}>
                    <line x1={0} x2={plotWidth} y1={y} y2={y} className="chart-grid-line" />
                    <text x={-8} y={y + 3} textAnchor="end" className="chart-axis-label">
                      {hasCost ? formatCurrency(tick) : formatCompact(tick)}
                    </text>
                  </g>
                );
              })}

              {dataDateIndex >= 0 && (
                <g>
                  <line
                    x1={xFor(dataDateIndex)}
                    x2={xFor(dataDateIndex)}
                    y1={-8}
                    y2={plotHeight}
                    stroke="var(--mr-vivid-blue)"
                    strokeWidth="1"
                    strokeDasharray="2 3"
                  />
                  <text
                    x={xFor(dataDateIndex) + 5}
                    y={-2}
                    className="chart-axis-label"
                    style={{ fill: 'var(--mr-vivid-blue)' }}
                  >
                    DATA DATE
                  </text>
                </g>
              )}

              {SERIES.map((series, seriesIndex) => {
                const path = buildPath(series.key);
                if (!path) return null;
                return (
                  <path
                    key={series.key}
                    d={path}
                    fill="none"
                    stroke={series.colour}
                    strokeWidth="2"
                    strokeDasharray={series.dash}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    className={series.dash ? undefined : 'series-line'}
                    style={
                      series.dash
                        ? undefined
                        : ({
                            // Rough path length; exact value only affects timing.
                            '--dash-length': `${plotWidth * 2}px`,
                            animationDelay: `${seriesIndex * 180}ms`,
                          } as React.CSSProperties)
                    }
                  />
                );
              })}

              {hoverIndex !== null && (
                <line
                  x1={xFor(hoverIndex)}
                  x2={xFor(hoverIndex)}
                  y1={0}
                  y2={plotHeight}
                  stroke="var(--border-strong)"
                  strokeWidth="1"
                />
              )}

              {hovered &&
                SERIES.map((series) => {
                  const value = hovered[series.key];
                  if (value === null || value === undefined) return null;
                  return (
                    <circle
                      key={series.key}
                      cx={xFor(hoverIndex!)}
                      cy={yFor(value)}
                      r="3.5"
                      fill="var(--surface-card)"
                      stroke={series.colour}
                      strokeWidth="2"
                    />
                  );
                })}

              {/* Invisible columns drive the hover, so the pointer never has
                  to land precisely on a 2px line. */}
              {curve.map((_, index) => (
                <rect
                  key={index}
                  x={xFor(index) - plotWidth / (curve.length - 1) / 2}
                  y={0}
                  width={plotWidth / (curve.length - 1)}
                  height={plotHeight}
                  fill="transparent"
                  onMouseEnter={() => setHoverIndex(index)}
                />
              ))}

              {curve.map((point, index) =>
                index % labelStride === 0 ? (
                  <text
                    key={point.label}
                    x={xFor(index)}
                    y={plotHeight + 16}
                    textAnchor="middle"
                    className="chart-axis-label"
                  >
                    {point.label}
                  </text>
                ) : null
              )}
            </g>
          </svg>

          <div
            className="mt-1 min-h-[42px] rounded-sm px-3 py-2 text-xs"
            style={{ background: 'var(--surface-inset)' }}
          >
            {hovered ? (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                <span className="font-semibold">{hovered.label}</span>
                {SERIES.map((series) => {
                  const value = hovered[series.key];
                  return (
                    <span key={series.key} className="tnum" style={{ color: series.colour }}>
                      {series.label.split(' ')[0]}:{' '}
                      {value === null || value === undefined ? '—' : format(value)}
                    </span>
                  );
                })}
              </div>
            ) : (
              <span style={{ color: 'var(--text-muted)' }}>
                Hover the chart for month-by-month values.
                {!hasCost &&
                  ' No cost data in this file — the curve counts activities instead.'}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
